import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { calculateSellerScore, type SellerScoreResult } from "@/lib/seller-score";
import { differenceInMonths } from "date-fns";
import { queryKeys } from "@/lib/query-keys";

export function useSellerTrustScore(sellerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.seller.trustScore(sellerId),
    queryFn: async (): Promise<SellerScoreResult> => {
      if (!sellerId) throw new Error("No seller ID");

      const { data, error } = await supabase.rpc("get_seller_trust_data", {
        p_seller_id: sellerId,
      });

      if (error) throw error;
      if (!data) throw new Error("Seller not found");

      const d = data as any;
      const createdAt = d.created_at ? new Date(d.created_at) : new Date();
      const accountAgeMonths = differenceInMonths(new Date(), createdAt);

      const commReviews = (d.commission_reviews as any[]) || [];
      const commissionAvgRating = commReviews.length > 0
        ? commReviews.reduce((sum: number, r: any) => sum + r.rating, 0) / commReviews.length
        : null;

      return calculateSellerScore({
        accountAgeMonths,
        // Volume counts everything buyers hold, free claims included; the refund
        // rate is over paid sales only. The old `total_sales` mixed the two,
        // which let a seller's free downloads dilute their refund rate to zero.
        totalSales: d.acquisitions ?? 0,
        averageRating: d.avg_rating ?? null,
        reviewCount: d.review_count || 0,
        assetCount: d.asset_count || 0,
        followerCount: d.follower_count || 0,
        refundCount: d.paid_refunds ?? 0,
        totalTransactions: d.paid_sales ?? 0,
        isEarlyFounder: d.is_early_founder ?? false,
        commissionCount: d.commission_count || 0,
        commissionAvgRating,
      });
    },
    enabled: !!sellerId,
    staleTime: 5 * 60 * 1000,
  });
}
